<template>
  <div class="chat-window d-flex flex-column flex-1 justify-center align-center position-relative">
    <!-- Header -->
    <div class="d-none d-lg-flex position-absolute left-0 top-0 width-100 pa-4 white--text">
      <v-menu offset-y rounded>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            color="primary"
            dark
            v-bind="attrs"
            v-on="on"
          >
            {{ model.description }} <v-icon right>mdi-chevron-down</v-icon>
          </v-btn>
        </template>
        <v-list class="ideaverse-black-highlight--background">
          <v-list-item two-line v-for="(item, index) in models" :key="index" @click="setProvider(item)">
            <v-list-item-content>
              <v-list-item-title class="white--text">{{ item.description }}</v-list-item-title>
              <v-list-item-subtitle class="grey--text">{{ item.provider }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
    <div class="d-flex d-lg-none width-100 pa-4 white--text">
      <v-menu offset-y rounded>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            color="primary"
            dark
            v-bind="attrs"
            v-on="on"
          >
            {{ model.description }} <v-icon right>mdi-chevron-down</v-icon>
          </v-btn>
        </template>
        <v-list class="ideaverse-black-highlight--background">
          <v-list-item two-line v-for="(item, index) in models" :key="index" @click="setProvider(item)">
            <v-list-item-content>
              <v-list-item-title class="white--text">{{ item.description }}</v-list-item-title>
              <v-list-item-subtitle class="grey--text">{{ item.provider }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
    <!-- Messages -->
    <div v-if="messages.length > 0" ref="scrollArea" class="messages px-3 py-2 width-100" style="max-width: 50rem">
      <div v-for="m in messages" :key="m.id" class="message-row" :class="m.role">
        <div class="bubble">
          <template v-if="m.role === 'thinking' || (m.id && m.id.startsWith('temp-assistant') && (!m.content || m.content.length === 0))">
            <div class="thinking">
              <div class="dots">
                <span></span><span></span><span></span>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="content">{{ m.content }}</div>
          </template>
          <div class="meta" v-if="!isTempMessage(m)">{{ formatTime(m.createdAt) }}</div>
        </div>
      </div>
    </div>
    <div v-else class="empty-state d-flex flex-column align-center justify-center">
      <div class="outfit-regular white--text font-size-30">How can I help?</div>            
    </div>

    <div class="message-input py-6">
      <v-textarea
        v-model="input"
        auto-grow
        rows="1"
        hide-details
        placeholder="Ask anything"
        class="flex-1 width-100 chat-input white--text"
        filled
        dark
        rounded
        @click:append-outer="send"
        @keydown.enter.exact.prevent="send"
        :loading="sending"
        :disabled="sending"
      >
        <template #append-outer>
          <v-btn
            v-if="input"
            class="mx-2 black--text"
            fab
            dark
            small
            color="yellow darken-3"
            elevation="0"
            @click="send"
          >
            <v-icon dark>
              mdi-arrow-up
            </v-icon>
          </v-btn>
          <v-btn
            v-if="!input"
            class="mx-2"
            fab
            :color="isRecording ? 'red' : 'yellow darken-3'"
            :class="isRecording ? 'white--text' : 'black--text'"
            dark
            small
            elevation="0"
            :loading="isProcessing"
            :disabled="isProcessing"
            @click="toggleMic"
            title="Click to start/stop recording"
          >
            <v-icon dark>
              {{ isRecording ? 'mdi-stop' : 'mdi-microphone-outline' }}
            </v-icon>
          </v-btn>
        </template>
        <template #append-inner-icon>
          <v-btn>inner</v-btn>
        </template>
      </v-textarea>
    </div>
  </div>
</template>

<script>
import { collection, query, orderBy, onSnapshot, addDoc, doc, setDoc, getDoc, updateDoc } from 'firebase/firestore'
import { db } from '@/firebase/init'

export default {
  name: 'ChatComponent',
  props: {
    agent: { type: String, default: 'bleepbloop' },
    context: { type: Object, default: () => ({}) },
    placeholder: { type: String, default: 'Ask anything' },
    introText: { type: String, default: 'How can I help?' },
    chatId: { type: String, default: null },
  },
  data() {
    return {
      messages: [],
      input: '',
      sending: false,
      model: { provider: 'OpenAI', model: 'ChatGPT 5', tier: 'fast', description: 'ChatGPT 5' },
      models: [
        { provider: 'Google', model: 'gemini-2.5-pro', tier: 'best', description: 'Gemini 2.5 Pro' },
        { provider: 'OpenAI', model: 'ChatGPT 5', tier: 'best', description: 'ChatGPT 5' },
        { provider: 'Anthropic', model: 'claude-4-sonnet', tier: 'fast', description: 'Claude 4 Sonnet' },
        { provider: 'xAI', model: 'grok-4', tier: 'best', description: 'Grok 4' },
      ],
      // voice state
      isRecording: false,
      isProcessing: false,
      transcriptionError: null,
      mediaRecorder: null,
      audioChunks: [],
      tempMessages: [],
      unsub: null,
      internalChatId: null,
    }
  },
  computed: {
    activeChatId() {
      return this.internalChatId || this.chatId || (this.$route && this.$route.params && this.$route.params.chatId) || null
    },
    provider() {
      return (this.model && this.model.provider ? this.model.provider : '').toLowerCase()
    },
    tier() {
      return (this.model && this.model.tier ? this.model.tier : 'fast').toLowerCase()
    },
  },
  watch: {
    chatId: {
      immediate: true,
      handler() { this.subscribeMessages() }
    },
    '$route.params.chatId': {
      immediate: true,
      handler() { this.subscribeMessages() }
    }
  },
  methods: {
    setProvider(item) { if (item && typeof item === 'object') this.model = { ...item } },
    isTempMessage(m) {
      try { return !!(m && typeof m.id === 'string' && m.id.startsWith('temp-')); } catch { return false }
    },
    formatTime(ts) {
      try { const d = ts?.toDate ? ts.toDate() : (ts instanceof Date ? ts : new Date()); return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) } catch { return '' }
    },
    async subscribeMessages() {
      if (this.unsub) { this.unsub(); this.unsub = null }
      this.messages = []
      if (!this.activeChatId) return
      const messagesCol = collection(db, 'chats', this.activeChatId, 'messages')
      const q = query(messagesCol, orderBy('createdAt', 'asc'))
      this.unsub = onSnapshot(q, (snap) => {
        this.messages = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        this.$nextTick(this.scrollToBottom)
      }, (err) => {
        console.error('messages snapshot error', err)
        this.$snackbar && this.$snackbar.error('Failed to load messages')
      })
    },
    scrollToBottom() {
      const el = this.$refs.scrollArea
      if (el) el.scrollTop = el.scrollHeight
    },
    addTempMessage(role, content) {
      const tempId = 'temp-' + Date.now()
      this.messages.push({ id: tempId, role, content, createdAt: new Date() })
      this.tempMessages.push(tempId)
      return tempId
    },
    removeTempMessages() {
      this.messages = this.messages.filter(msg => !this.tempMessages.includes(msg.id))
      this.tempMessages = []
    },
    buildHistoryForBackend() {
      // Map local 'assistant' to backend role expected by sparky
      return this.messages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({ role: m.role === 'assistant' ? 'sparky' : 'user', content: m.content }))
        .slice(-24)
    },
    async ensureChat() {
      if (this.activeChatId) return this.activeChatId
      const uid = this.authUser && this.authUser.uid
      if (!uid) throw new Error('Not signed in')
      const ref = doc(collection(db, 'chats'))
      await setDoc(ref, { ownerId: uid, title: '', createdAt: new Date(), updatedAt: new Date(), searchText: '' })
      this.internalChatId = ref.id
      this.$emit('created', ref.id)
      // Immediately subscribe so messages don't briefly disappear on new chats
      this.subscribeMessages()
      return ref.id
    },
    async send() {
      if (!this.input || !this.input.trim()) return
      if (!this.authUser) { this.$snackbar && this.$snackbar.error('Please sign in'); return }
      this.sending = true
      const content = this.input.trim()
      this.input = ''
      let chatId = null
      try {
        chatId = await this.ensureChat()
        const messagesCol = collection(db, 'chats', chatId, 'messages')
        await addDoc(messagesCol, { role: 'user', content, createdAt: new Date() })
        this.$nextTick(this.scrollToBottom)

        const token = await this.$root.user.getIdToken()
        const history = this.buildHistoryForBackend()
        const [url, requestInit, streaming] = this.buildRequest(token, content, history)

        // Show thinking while awaiting first bytes
        const thinkingId = 'temp-thinking-' + Date.now()
        this.messages.push({ id: thinkingId, role: 'thinking', content: '', createdAt: new Date() })

        const res = await fetch(url, requestInit)
        const contentType = res.headers.get('content-type') || ''

        // Non-streaming JSON
        if (!streaming || contentType.includes('application/json')) {
          const json = await res.json().catch(()=>({}))
          // Remove thinking
          this.messages = this.messages.filter(m => m.id !== thinkingId)
          if (!res.ok) throw new Error(json.error || 'Model call failed')
          const text = typeof json === 'string' ? json : (json.message || json.text || JSON.stringify(json))
          await addDoc(messagesCol, { role: 'assistant', content: text, createdAt: new Date() })
          // update chat doc search/title
          await this.updateChatMeta(chatId, content, text)
          this.$nextTick(this.scrollToBottom)
          return
        }

        // Streaming SSE
        // Replace thinking with temp assistant message for streaming
        this.messages = this.messages.filter(m => m.id !== thinkingId)
        const tempId = 'temp-assistant-' + Date.now()
        this.messages.push({ id: tempId, role: 'assistant', content: '', createdAt: new Date() })
        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let finalResponseText = ''
        let errorText = ''
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          const rawChunk = decoder.decode(value)
          const lines = rawChunk.split('\n')
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const jsonData = JSON.parse(line.substring(6))
                if (jsonData.chunk) {
                  const tempMsg = this.messages.find(m => m.id === tempId)
                  if (tempMsg) {
                    tempMsg.content += jsonData.chunk
                    finalResponseText += jsonData.chunk
                    this.scrollToBottom()
                  }
                }
                if (jsonData.error) {
                  errorText = typeof jsonData.error === 'string' ? jsonData.error : (jsonData.error.details || 'Unknown error')
                }
              } catch (e) { /* ignore */ }
            }
          }
        }
        const text = finalResponseText.trim()
        // Remove temp streaming msg
        this.messages = this.messages.filter(m => m.id !== tempId)
        if (text) {
          await addDoc(messagesCol, { role: 'assistant', content: text, createdAt: new Date() })
          await this.updateChatMeta(chatId, content, text)
        } else {
          const msg = errorText ? `Sorry, something went wrong: ${errorText}` : 'Sorry, I could not generate a reply this time.'
          await addDoc(messagesCol, { role: 'assistant', content: msg, createdAt: new Date() })
        }
        this.$nextTick(this.scrollToBottom)
      } catch (e) {
        console.error('send error', e)
        this.$snackbar && this.$snackbar.error(e.message)
      } finally {
        this.sending = false
      }
    },
    buildRequest(token, prompt, history) {
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      if (this.agent === 'sparky') {
        const context = { page: 'game', isNew: !this.context?.gameId, ...(this.context || {}) }
        const body = { provider: this.provider, tier: this.tier, stream: true, prompt, history, context }
        return [this.$endpoints.sparkyChat, { method: 'POST', headers: { ...headers, 'Accept': 'text/event-stream' }, body: JSON.stringify(body) }, true]
      }
      // default: ideaverseInvoke for specialized agents
      const body = { agent: this.agent, provider: this.provider, tier: this.tier, prompt, history, ...this.context }
      return [this.$endpoints.ideaverseInvoke, { method: 'POST', headers, body: JSON.stringify(body) }, false]
    },
    async updateChatMeta(chatId, userText, assistantText) {
      try {
        const chatRef = doc(db, 'chats', chatId)
        const safe = (s) => (typeof s === 'string' ? s : JSON.stringify(s || ''))
        const append = [safe(userText), safe(assistantText)].join(' \n ')
        const chatSnap = await getDoc(chatRef)
        const prev = chatSnap.exists() ? (chatSnap.data().searchText || '') : ''
        const nextSearch = [prev, append].filter(Boolean).join(' \n ').slice(-10000)
        await updateDoc(chatRef, { updatedAt: new Date(), title: userText.slice(0, 60), searchText: nextSearch })
      } catch (e) { /* non-fatal */ }
    },
    async requestMicrophonePermission() {
      try { await navigator.mediaDevices.getUserMedia({ audio: true }); return true } catch (err) {
        console.error('Mic permission error:', err.name, err.message)
        this.transcriptionError = err?.message || 'Mic error'
        return false
      }
    },
    async startRecording() {
      if (this.isRecording || this.isProcessing) return
      if (!await this.requestMicrophonePermission()) return
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        const options = { mimeType: 'audio/webm; codecs=opus' }
        if (!MediaRecorder.isTypeSupported(options.mimeType)) {
          options.mimeType = 'audio/webm'
          if (!MediaRecorder.isTypeSupported(options.mimeType)) {
            this.addTempMessage('system', 'No suitable audio format supported.')
            setTimeout(() => this.removeTempMessages(), 5000)
            stream.getTracks().forEach(t => t.stop())
            return
          }
        }
        this.mediaRecorder = new MediaRecorder(stream, options)
        this.audioChunks = []
        this.mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) this.audioChunks.push(e.data) }
        this.mediaRecorder.onstop = async () => {
          stream.getTracks().forEach(t => t.stop())
          if (this.audioChunks.length === 0) { this.isRecording = false; return }
          this.isProcessing = true
          const audioBlob = new Blob(this.audioChunks, { type: this.mediaRecorder.mimeType })
          this.audioChunks = []
          try {
            const token = await this.$root.user.getIdToken()
            const formData = new FormData()
            formData.append('audio', audioBlob, 'recording.webm')
            const response = await fetch(this.$endpoints.transcribeAudio, { method: 'POST', body: formData, headers: { 'Authorization': `Bearer ${token}` } })
            const responseBodyText = await response.text()
            if (!response.ok) {
              let errData = { error: `Server error: ${response.status}` }
              try { errData = JSON.parse(responseBodyText) } catch (e) { errData.error = responseBodyText || errData.error }
              throw new Error(errData.error)
            }
            const result = JSON.parse(responseBodyText)
            if (result.transcript !== undefined) {
              this.input = (this.input ? this.input + ' ' : '') + String(result.transcript).trim()
            } else if (result.error) {
              this.$snackbar && this.$snackbar.error(result.error)
            } else {
              this.$snackbar && this.$snackbar.error('Unexpected response from transcription service.')
            }
          } catch (err) {
            console.error('Transcription fetch error:', err)
            this.$snackbar && this.$snackbar.error(err.message || 'Transcription failed.')
          } finally {
            this.isProcessing = false
          }
        }
        this.mediaRecorder.start()
        this.isRecording = true
      } catch (err) {
        console.error('startRecording setup error:', err)
        this.$snackbar && this.$snackbar.error('Failed to start recording.')
      }
    },
    stopRecording() {
      if (this.mediaRecorder && this.isRecording) {
        this.mediaRecorder.stop()
        this.isRecording = false
      }
    },
    toggleMic() { if (this.isRecording) this.stopRecording(); else this.startRecording() }
  },
  beforeDestroy() {
    if (this.unsub) this.unsub()
    if (this.mediaRecorder) {
      try {
        this.mediaRecorder.onstop = null
        if (this.mediaRecorder.state && this.mediaRecorder.state !== 'inactive') this.mediaRecorder.stop()
      } catch (e) { /* noop */ }
      if (this.mediaRecorder.stream) this.mediaRecorder.stream.getTracks().forEach(track => track.stop())
      this.mediaRecorder = null
    }
    this.isRecording = false
    this.isProcessing = false
  }
}
</script>

<style scoped>
.chat-window { min-height: 0; overflow-y: hidden; position: relative; }
.messages { overflow-y: scroll; min-height: 0; height: calc(100vh - 170px); }
.messages-sm { overflow-y: scroll; min-height: 0; height: calc(100vh - 236px); }
.message-row { display: flex; margin: 8px 0; }
.message-row.user { justify-content: flex-end; }
.message-row.assistant { justify-content: flex-start; }
.bubble { padding: 12px 14px; border-radius: 14px; background: #202124; color: #eaeaea; word-wrap: break-word; white-space: pre-wrap; }
.message-row.user .bubble { background: #1a73e8; color: white; max-width: 68%; }
.message-row.assistant .bubble { background: #2b2b2b; }
.meta { opacity: 0.6; font-size: 11px; margin-top: 6px; }
.thinking { display: flex; align-items: center; padding: 4px 0; }
.dots span { display: inline-block; width: 6px; height: 6px; margin-right: 4px; border-radius: 50%; background: #9aa0a6; animation: blink 1.2s infinite ease-in-out; }
.dots span:nth-child(2) { animation-delay: 0.2s; }
.dots span:nth-child(3) { animation-delay: 0.4s; }
@keyframes blink { 0%, 80%, 100% { opacity: 0.2 } 40% { opacity: 1 } }
.chat-input { background: #303030 !important; color: white !important; }
.chat-input :deep(.v-input__slot),
.chat-input :deep(.v-input__slot:hover),
.chat-input :deep(.v-input--is-focused .v-input__slot),
.chat-input :deep(.v-input__slot:focus),
.chat-input :deep(.v-input__slot:focus-within) { background: #303030 !important; box-shadow: none !important; }
.chat-input :deep(.v-input__append-outer) { margin-top: 8px; margin-right: 5px; }
.message-input { width: 100%; max-width: 50rem; padding: 0 12px; }
</style>